<script setup lang="ts">
import { computed, getCurrentInstance, ref, unref } from 'vue';
import ComplianceSummaryRow from './ComplianceSummaryRow.vue';
import ComplianceMappingRow from './ComplianceMappingRow.vue';
import ExpandToggleButton from '@/_core/shared/ui/ExpandToggleButton/ExpandToggleButton.vue';
import type {
  TComplianceRequirement,
  TComplianceRequirementItem,
  TComplianceSummary,
  TComplianceMappingsResponse,
} from '@/_core/entities/report/models/types';

const props = defineProps<{
  summary?: TComplianceSummary;
  mappings?: TComplianceMappingsResponse;
  mode?: 'summary' | 'mappings';
  isLoading: boolean;
  integrationId?: string;
  selectedRequirementId?: string;
  selectedItemId?: string;
}>();

const emit = defineEmits<{
  (e: 'viewFindings', payload: { requirementId: string; itemId?: string }): void;
}>();

const isMappingsMode = computed(() => props.mode === 'mappings');

const widgetInstance = getCurrentInstance();
const complianceScopeRoute = computed(() => {
  if (!props.integrationId) return null;
  const currentPath = unref(widgetInstance?.proxy.$route)?.fullPath ?? '';
  const from = currentPath ? `?from=${encodeURIComponent(currentPath)}` : '';
  return `/integration/${props.integrationId}/settings/compliance-scope${from}`;
});

const expanded = ref<Record<string, boolean>>({});

const requirements = computed(() => {
  if (isMappingsMode.value) return props.mappings?.requirements ?? [];
  return props.summary?.requirements ?? [];
});

const allExpanded = computed(
  () => requirements.value.length > 0 && requirements.value.every((r) => expanded.value[r.id])
);

function toggle(id: string) {
  expanded.value = { ...expanded.value, [id]: !expanded.value[id] };
}

function toggleAll() {
  if (allExpanded.value) {
    expanded.value = {};
  } else {
    const next: Record<string, boolean> = {};
    for (const r of requirements.value) next[r.id] = true;
    expanded.value = next;
  }
}

const SUMMARY_HEADER_GRID =
  'tw:grid-cols-[1.5rem_minmax(10rem,1fr)_4rem_minmax(10rem,22rem)_6rem] ' +
  'tw:max-[890px]:grid-cols-[1.5rem_minmax(0,1fr)_7rem_9rem] ' +
  'tw:max-[576px]:grid-cols-[1.5rem_minmax(0,1fr)_auto_auto]';

const MAPPINGS_HEADER_GRID =
  'tw:grid-cols-[1.5rem_minmax(10rem,1fr)_minmax(16rem,24rem)] tw:max-[500px]:hidden';
</script>

<template>
  <div>
    <div class="tw:flex tw:items-start tw:justify-between tw:gap-4 tw:max-[439px]:flex-col tw:max-[439px]:items-start">
      <div class="tw:flex tw:items-center tw:flex-wrap tw:gap-3">
        <div class="tw:typography-h1">
          {{ isMappingsMode ? 'Compliance Mappings' : 'Compliance Requirements' }}
        </div>

        <template v-if="!isLoading && !isMappingsMode && summary">
          <span
            v-if="requirements.length === 0"
            class="tw:px-3 tw:py-1 tw:rounded-md tw:text-xs tw:bg-[#fdece7] tw:text-[#e84a2a] tw:whitespace-nowrap"
          >No selected documents</span>
          <template v-else>
            <span class="tw:py-1 tw:px-3 tw:rounded-sm tw:typography-sidenotes tw:text-main-blue tw:bg-blue-bg tw:whitespace-nowrap">
              {{ summary.totalFindings }} findings
            </span>
            <span class="tw:py-1 tw:px-3 tw:rounded-sm tw:typography-sidenotes tw:text-main-blue tw:bg-blue-bg tw:whitespace-nowrap">
              {{ requirements.length }} documents selected
            </span>
          </template>
        </template>
      </div>

      <router-link
        v-if="complianceScopeRoute"
        :to="complianceScopeRoute"
        class="tw:shrink-0 tw:px-4.5 tw:py-1.5 tw:typography-small tw:text-main-blue tw:border tw:border-main-blue/30 tw:rounded-3xl tw:lg:hover:bg-main-blue/10 tw:lg:transition-colors tw:whitespace-nowrap tw:no-underline tw:hover:no-underline"
      >Change Compliance Scope</router-link>
    </div>

    <div class="tw:mt-2 tw:text-secondary tw:typography-small">
      {{ isMappingsMode
        ? 'Shows which selected compliance documents match this vulnerability.'
        : 'Shows how findings in the report map to the compliance documents selected for this app.' }}
    </div>

    <div v-if="isLoading" class="tw:mt-4 tw:text-secondary tw:typography-small">Loading...</div>

    <div v-else-if="requirements.length > 0" class="tw:mt-4">
      <div class="tw:flex tw:justify-end tw:mb-2">
        <ExpandToggleButton
          :value="allExpanded"
          collapsed-text="Expand all"
          expanded-text="Collapse all"
          @input="toggleAll"
        />
      </div>

      <!-- Summary header -->
      <div
        v-if="!isMappingsMode"
        :class="'tw:hidden tw:md:grid tw:px-6 tw:py-2 tw:gap-6 tw:text-[#949DB8] tw:typography-small ' + SUMMARY_HEADER_GRID"
      >
        <div></div>
        <div class="tw:pl-6 tw:max-[890px]:pl-0">Name</div>
        <div class="tw:text-right tw:max-[890px]:hidden">Findings</div>
        <div>Severity Distribution</div>
        <div></div>
      </div>

      <!-- Mappings header -->
      <div
        v-else
        :class="'tw:hidden tw:md:grid tw:px-6 tw:py-2 tw:gap-6 tw:text-[#949DB8] tw:typography-small ' + MAPPINGS_HEADER_GRID"
      >
        <div></div>
        <div>Name</div>
        <div>Relevance</div>
      </div>

      <!-- Rows -->
      <div class="tw:flex tw:flex-col">
        <template v-if="isMappingsMode">
          <ComplianceMappingRow
            v-for="req in requirements"
            :key="req.id"
            :requirement="req"
            :expanded="!!expanded[req.id]"
            @toggle="toggle(req.id)"
          />
        </template>
        <template v-else>
          <ComplianceSummaryRow
            v-for="req in requirements"
            :key="req.id"
            :requirement="req"
            :expanded="!!expanded[req.id]"
            :selected-requirement-id="selectedRequirementId"
            :selected-item-id="selectedItemId"
            @toggle="toggle(req.id)"
            @viewRequirement="emit('viewFindings', { requirementId: req.id })"
            @viewItem="emit('viewFindings', { requirementId: req.id, itemId: $event.id })"
          />
        </template>
      </div>
    </div>
  </div>
</template>
